import { useEffect } from 'react';
import { TrafficAllocationVariant } from '@app/react-common/components/traffic-allocation/TrafficAllocationShared';
import { AbTest } from '../../../models/AbTest';
import { AbTestVariant } from '../../../models/AbTestVariant';
import { AbTestWidgetType } from '../../../models/AbTestCreationType';
import { AbTestFormData } from '../AbTestModal';
import { ShopifyTheme } from './useThemes';
import { ProductPage } from './useProductPages';
import { SmartCart } from './useSmartCarts';

interface UseThemeVariantsParams {
  themes?: ShopifyTheme[];
  productPages?: ProductPage[];
  smartCarts?: SmartCart[];
  widgetType: AbTestWidgetType;
  existingAbTest?: AbTest;
  mode: 'create' | 'edit' | 'view' | 'duplicate';
  isThemeBasedTest: boolean;
  setFormData: React.Dispatch<React.SetStateAction<AbTestFormData>>;
}

/**
 * Custom hook that manages theme or product page variants for A/B tests
 * Converts theme/product page data into traffic allocation variants and updates form data
 */
const useThemeVariants = ({
  themes,
  productPages,
  smartCarts,
  widgetType,
  existingAbTest,
  mode,
  isThemeBasedTest,
  setFormData,
}: UseThemeVariantsParams) => {
  useEffect(() => {
    if (isThemeBasedTest) {
      // themes → CROSS_SITE, smartCarts → SMART_CART, productPages → PRODUCT_PAGE
      const items =
        widgetType === AbTestWidgetType.CROSS_SITE
          ? themes
          : widgetType === AbTestWidgetType.SMART_CART
            ? smartCarts
            : productPages;

      if (items && items.length > 0) {
        setFormData((prevData) => {
          const variants = items.map((item, idx) => {
            const existingAbTestVariant: AbTestVariant = existingAbTest?.abTestVariants?.find((variant) =>
              variant.abTestWidgets?.find((widget) => {
                if (
                  widget.type !== AbTestWidgetType.CROSS_SITE &&
                  widget.type !== AbTestWidgetType.PRODUCT_PAGE &&
                  widget.type !== AbTestWidgetType.SMART_CART
                ) {
                  return false;
                }
                // Handle null case for "No Product Page" option
                if (item.id === null) {
                  return widget.externalId === null;
                }
                // Convert both to Number to handle BIGINT values that may be serialized as strings from MySQL
                // to prevent precision loss, even though both are typed as numbers
                return Number(widget.externalId) === Number(item.id);
              }),
            );

            // Themes have updatedAt and previewUrl, ProductPages have createdAt and no previewUrl
            const isCrossSite = widgetType === AbTestWidgetType.CROSS_SITE;
            const themeItem = item as ShopifyTheme;
            const productPageItem = item as ProductPage;

            const trafficAllocationVariant: TrafficAllocationVariant = {
              apiId: null,
              widgetExternalId: item.id,
              name: item.name,
              description: isCrossSite ? themeItem.updatedAt || '' : '',
              percentageTraffic: mode === 'create' && idx < 2 ? 50 : existingAbTestVariant?.percentageTraffic || 0,
              isSelected: mode === 'create' ? idx < 2 : !!existingAbTestVariant,
              widgetApiId: undefined,
              previewUrl: isCrossSite ? themeItem.previewUrl : undefined,
              updatedAt: isCrossSite ? themeItem.updatedAt || '' : '',
            };

            if (existingAbTestVariant) {
              trafficAllocationVariant.isSelected = true;
              trafficAllocationVariant.percentageTraffic = existingAbTestVariant.percentageTraffic;
              trafficAllocationVariant.apiId = existingAbTestVariant.id;
              const widget = existingAbTestVariant.abTestWidgets?.find(
                (x) =>
                  x.type === AbTestWidgetType.CROSS_SITE ||
                  x.type === AbTestWidgetType.PRODUCT_PAGE ||
                  x.type === AbTestWidgetType.SMART_CART,
              );
              trafficAllocationVariant.widgetApiId = widget?.id;

              // Extract checkout group IDs from widget metadata (new approach) or from checkout widgets (backwards compatibility)
              if (widget?.metadata?.checkoutGroupIds && widget.metadata.checkoutGroupIds.length > 0) {
                // New approach: Read from metadata JSON field
                // Convert to Number to handle BIGINT values that may be serialized as strings from MySQL
                trafficAllocationVariant.checkoutGroupIds = widget.metadata.checkoutGroupIds.map((id) => Number(id));
              } else {
                // Backwards compatibility: Read from separate checkout widgets
                const checkoutWidgets = existingAbTestVariant.abTestWidgets?.filter(
                  (widget) => widget.type === AbTestWidgetType.CHECKOUT,
                );
                if (checkoutWidgets?.length > 0) {
                  // Convert to Number to handle BIGINT values that may be serialized as strings from MySQL
                  trafficAllocationVariant.checkoutGroupIds = checkoutWidgets.map((widget) =>
                    Number(widget.externalId),
                  );
                }
              }
            }

            return trafficAllocationVariant;
          });

          return {
            ...prevData,
            abTestVariants: variants,
          };
        });
      }
    }
  }, [themes, productPages, smartCarts, widgetType, existingAbTest, mode, isThemeBasedTest, setFormData]);
};

export default useThemeVariants;
